import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DonutChart } from '@tremor/react';
import { useNavigate } from 'react-router-dom';
import styles from './ProductStatusChart.module.scss';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useDashboardTranslations } from '@/hooks/useDashboardTranslations';
import { useTheme } from '@/contexts/ThemeContext';

interface ProductStatusChartProps {
  activeProducts: number;
  inactiveProducts: number;
  loading: boolean;
  className?: string;
}

export const ProductStatusChart: React.FC<ProductStatusChartProps> = ({
  activeProducts,
  inactiveProducts,
  loading,
  className,
}) => {
  const navigate = useNavigate();
  const i18n = useDashboardTranslations();
  const { actualTheme } = useTheme();

  // Calculate the percentage of active products
  const totalProducts = activeProducts + inactiveProducts;
  const activePercentage =
    totalProducts > 0 ? Math.round((activeProducts / totalProducts) * 100) : 0;

  // Theme-aware button styling
  const buttonClassName =
    actualTheme === 'dark'
      ? 'flex h-auto min-w-[110px] items-center justify-center px-4 py-2 text-xs bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 hover:border-gray-500'
      : 'flex h-auto min-w-[110px] items-center justify-center px-4 py-2 text-xs';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card
            className={`h-full border-enterprise-200 bg-white shadow-sm transition-all hover:translate-y-[-2px] hover:shadow-md dark:border-border dark:bg-card ${className || ''}`}
            tabIndex={0}
            aria-label={i18n.getProductStatusText('title')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                {i18n.getProductStatusText('title')}
              </CardTitle>
              {loading ? (
                <Skeleton className="h-4 w-36" />
              ) : (
                <CardDescription className="text-enterprise-500 dark:text-muted-foreground">
                  {i18n.getProductStatusText('activeProducts', {
                    count: activeProducts,
                  })}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="h-[calc(100%-76px)] overflow-auto pt-2">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-4">
                  <Skeleton className="size-40 rounded-full" />
                  <div className="mt-4 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex w-full flex-col items-center">
                    <div className="relative mb-2 flex size-48 items-center justify-center">
                      <DonutChart
                        data={[
                          {
                            name: i18n.getProductStatusText('active'),
                            value: activeProducts,
                            color: 'rgb(34, 197, 94)',
                          },
                          {
                            name: i18n.getProductStatusText('inactive'),
                            value: inactiveProducts,
                            color:
                              actualTheme === 'dark' ? 'rgb(75, 85, 99)' : 'rgb(242, 242, 242)',
                          },
                        ]}
                        className={`size-44 ${styles['hide-tremor-legend']} pointer-events-none`}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: '90px',
                          height: '90px',
                          borderRadius: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 1,
                        }}
                        className="bg-card dark:!bg-card"
                      />
                      <div
                        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                        style={{ zIndex: 2 }}
                      >
                        <div className="text-3xl font-bold leading-tight text-enterprise-900 dark:text-foreground">
                          {activePercentage}%
                        </div>
                        <div className="mt-1 text-xs font-medium text-enterprise-500 dark:text-muted-foreground">
                          {i18n.getProductStatusText('active')}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        className={buttonClassName}
                        onClick={() => navigate('/app/products?status=active')}
                      >
                        <div className="mr-2 size-2 rounded-full bg-success-500"></div>
                        {i18n.getProductStatusText('activeButton', {
                          count: activeProducts,
                        })}
                      </Button>
                      <Button
                        variant="outline"
                        className={buttonClassName}
                        onClick={() => navigate('/app/products?status=inactive')}
                      >
                        <div
                          className={`mr-2 size-2 rounded-full ${actualTheme === 'dark' ? 'bg-gray-500' : 'bg-enterprise-200'}`}
                        ></div>
                        {i18n.getProductStatusText('inactiveButton', {
                          count: inactiveProducts,
                        })}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent side="top">{i18n.getProductStatusText('tooltip')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
